import { useState, useEffect } from 'react';
import { useDevice } from './useDevice';
import { logger } from '../lib/logger';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const { os, browserName } = useDevice();

  const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as any).standalone ||
    document.referrer.includes('android-app://');

  const isIOSSafari = os === 'iOS' && browserName === 'Safari';
  const isAndroid = os === 'Android';

  const supportsNativePrompt = 'BeforeInstallPromptEvent' in window;

  useEffect(() => {
    if (isStandalone) return;

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    const handleAppInstalled = () => {
      setDeferredPrompt(null);
      setIsInstalled(true);
      logger.warn('[PWA] App was installed');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isStandalone]);

  const promptInstall = async (): Promise<'accepted' | 'dismissed' | 'manual' | 'unavailable'> => {
    if (isIOSSafari && !isStandalone) {
      return 'manual';
    }

    if (!deferredPrompt) {
      return 'manual';
    }

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      return outcome;
    } catch (error) {
      logger.warn('[PWA] Error showing install prompt:', { data: { error } });
      return 'unavailable';
    }
  };

  const hasNativePrompt = deferredPrompt !== null;

  const isInstallable =
    !isStandalone &&
    !isInstalled &&
    'serviceWorker' in navigator &&
    (isIOSSafari || isAndroid || hasNativePrompt);

  return {
    isInstallable,
    isIOSSafari,
    isAndroid,
    hasNativePrompt,
    promptInstall
  };
};
